import { StringsKeys } from "@/core/internationalization/strings";
import Exception from "@/core/exceptions/Exception";
import { ScreeningReport } from "@/core/customers/entities/CustomerAnalysis";
import { Account } from "@/core/customers/entities/Accounts";
import { CustomerKYCData } from "@/core/customers/entities/CustomerKYC";
import { CustomerDocument } from "@/core/customers/entities/CustomerDocuments";
import { CustomerKYCUpdateDTO } from "@/core/customers/entities/CustomerKYCUpdate";
import ScreeningReportsRepository from "@/core/customers/repositories/ScreeningReportsRepository";
import AccountsRepository from "@/core/customers/repositories/AccountsRepository";
import CustomerKYCRepository from "@/core/customers/repositories/CustomerKYCRepository";
import CustomerDocumentsRepository from "@/core/customers/repositories/CustomerDocumentsRepository";
import type { IContactVerificationService } from "@/core/requests/services/ContactVerificationService";

/**
 * OTP result type returned from store actions.
 * UI layer is responsible for displaying toast notifications.
 */
export type OTPSendResult = {
  success: boolean;
  challengeId?: string;
  errorMessage?: string;
};

export type OTPVerifyResult = {
  success: boolean;
  errorMessage?: string;
};

export type KYCUpdateResult = {
  success: boolean;
  errorMessage?: string;
};

export type AdditionalInformationUpdates = Pick<
  CustomerKYCUpdateDTO,
  | "situationPatrimoniale"
  | "sourceDesFonds"
  | "objetRelationBancaire"
  | "residenceFiscalePays"
  | "ppe"
  | "fonctionPpeExercee"
  | "beneficiaireEffectif"
  | "identiteBeneficiaireEffectif"
>;

/**
 * Type definition for fetch action return value
 */
export type FetchResult<T> = {
  data: T;
};

/**
 * Type definition for fetch action function
 */
export type FetchAction<T> = (
  translate: (key: StringsKeys) => string,
  businessKey: string,
) => Promise<FetchResult<T>>;

/**
 * Identifies which contact field is being updated via OTP.
 */
export type ContactField =
  | "phone1"
  | "phone2"
  | "phone3"
  | "phone4"
  | "email1"
  | "email2";

/**
 * Helper type to extract action methods from state
 */
export type StateActions = {
  fetchScreeningReports: (
    translate: (key: StringsKeys) => string,
    businessKey: string,
  ) => Promise<void>;
  fetchAccountsOverview: (
    translate: (key: StringsKeys) => string,
    businessKey: string,
  ) => Promise<void>;
  fetchKYC: (
    translate: (key: StringsKeys) => string,
    businessKey: string,
  ) => Promise<void>;
  fetchAccounts: (
    translate: (key: StringsKeys) => string,
    businessKey: string,
  ) => Promise<void>;
  fetchDocuments: (
    translate: (key: StringsKeys) => string,
    businessKey: string,
  ) => Promise<void>;
  reset: () => void;

  updateCustomerKYC: (
    businessKey: string,
    updates: Partial<CustomerKYCUpdateDTO>,
  ) => Promise<KYCUpdateResult>;
  updateAdditionalInformation: (
    businessKey: string,
    updates: AdditionalInformationUpdates,
  ) => Promise<KYCUpdateResult>;

  sendUpdateOTP: (
    field: ContactField,
    destination: string,
  ) => Promise<OTPSendResult>;
  verifyUpdateOTP: (
    field: ContactField,
    code: string,
    challengeId: string,
  ) => Promise<OTPVerifyResult>;

  resetUpdateOTPState: () => void;
  setUpdateFieldVerified: (field: ContactField, verified: boolean) => void;
};

/**
 * State interface for FicheClient360 store
 */
export interface FicheClient360State extends StateActions {
  screeningReports: ScreeningReport[];
  accountsOverview: Account[];
  screeningReportsLoading: boolean;
  accountsOverviewLoading: boolean;
  screeningReportsError: Exception | null;
  accountsOverviewError: Exception | null;

  kycData: CustomerKYCData | null;
  kycLoading: boolean;
  kycError: Exception | null;

  accounts: Account[];
  accountsLoading: boolean;
  accountsError: Exception | null;

  documents: CustomerDocument[];
  documentsLoading: boolean;
  documentsError: Exception | null;

  updateLoading: boolean;
  updateError: Exception | null;
  updatePhone1Verified: boolean;
  updatePhone2Verified: boolean;
  updatePhone3Verified: boolean;
  updatePhone4Verified: boolean;
  updateEmail1Verified: boolean;
  updateEmail2Verified: boolean;
  updatePhone1ChallengeId: string | null;
  updatePhone2ChallengeId: string | null;
  updatePhone3ChallengeId: string | null;
  updatePhone4ChallengeId: string | null;
  updateEmail1ChallengeId: string | null;
  updateEmail2ChallengeId: string | null;
}

/**
 * Dependencies required to construct the FicheClient360 store.
 * This allows better testability by injecting fake repositories/services.
 */
export interface FicheClient360StoreDeps {
  screeningReportsRepository: ScreeningReportsRepository;
  accountsRepository: AccountsRepository;
  customerKYCRepository: CustomerKYCRepository;
  customerDocumentsRepository: CustomerDocumentsRepository;
  contactVerificationService: IContactVerificationService;
}

/**
 * Initial state for the store
 */
export const initialState: Omit<FicheClient360State, keyof StateActions> = {
  screeningReports: [],
  accountsOverview: [],
  screeningReportsLoading: false,
  accountsOverviewLoading: false,
  screeningReportsError: null,
  accountsOverviewError: null,
  kycData: null,
  kycLoading: false,
  kycError: null,
  accounts: [],
  accountsLoading: false,
  accountsError: null,
  documents: [],
  documentsLoading: false,
  documentsError: null,
  updateLoading: false,
  updateError: null,
  updatePhone1Verified: false,
  updatePhone2Verified: false,
  updatePhone3Verified: false,
  updatePhone4Verified: false,
  updateEmail1Verified: false,
  updateEmail2Verified: false,
  updatePhone1ChallengeId: null,
  updatePhone2ChallengeId: null,
  updatePhone3ChallengeId: null,
  updatePhone4ChallengeId: null,
  updateEmail1ChallengeId: null,
  updateEmail2ChallengeId: null,
};
